import { ArrowBack, ArrowForward, FormatListBulleted, LocationOn } from '@mui/icons-material';
import {
    Alert,
    AlertTitle,
    Box,
    Card,
    Divider,
    Grid,
    IconButton,
    MenuItem,
    Select,
    Snackbar,
    TablePagination,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Button, Typography, UtilityIcon } from '@uhc-tempo/components';
import range from 'lodash/range';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import FulfillmentService from '../../../API/FulfillmentService';
import { FormContext } from '../../../context/Form.context';
import '../../../styles/cssStyles.css';
import styles from '../../../styles/PageStyles.js';
import theme from '../../../styles/PageTheme';
import useDetectMobile from '../../../utils/customHooks/useDetectMobile';
import { formatFulfillmentRequest } from '../../../utils/FormatFunctions';
import allLocationsConst from '../../home/mockData/allLocations';
import CopyEstimate from './CopyEstimate.component';
import Estimate from './Estimate.component';
import GeoAverageCost from './GeoAvgCost.component';
import MapView from './MapView.component';
import OtherLocationsModal from './OtherLocationsModal.component';
import { useHistory } from 'react-router-dom';
import sendEstimateButtonLabel from '../../../utils/customHooks/sendEstimateButtonLabel';
import { PER_UNIT_MEASURES, SEARCH_ROUTE, SINGLE_ESTIMATE_ROUTE } from '../../../constants';
import {
    checkForHidingSendEstimateButton,
    getServiceNameToDisplay,
} from '../../../utils/EstimatePageUtil';
import PrintEstimateOrResultsButton from './PrintEstimateOrResultsButton';
import EstimateToggleComponent from './EstimateToggle.component';
import { checkIndemnityCoverages } from '../../../utils/SearchPageUtil';
import PerUnitDisclaimer from './PerUnitDisclaimer.js';
import { AgeRestrictionDisclaimer } from './AgeRestrictionMessageUtil.js';
import { apm } from '@elastic/apm-rum';

const classes = styles(theme);

export default function EstimatesPage(props) {
    const {
        setLoading,
        setProvider,
        currentSelectedServiceForDisplay,
        setCurrentSelectedServiceForDisplay,
        otherLocationsOpen,
        setOtherLocationsOpen,
        closestLocationIndex,
        setClosestLocationIndex,
        topLocation,
        setTopLocation,
        loading,
        provider,
    } = props;

    const {
        allProvidersPool,
        estimateResponse,
        formValues,
        handleChangeCustom,
        integrationType,
        setSingleEstimateResponse,
        memberDetails,
        getIncrementedCorId,
        clearPreventiveFlags,
        estimateResultsPage,
        setEstimateResultsPage,
    } = useContext(FormContext);

    const ref = useRef(null);
    const isDesktopView = useDetectMobile(ref);
    const history = useHistory();
    const { selectedProvider, selectedService } = formValues;
    const isIndemnity = checkIndemnityCoverages(memberDetails, true);

    // Manual page load apm transaction, used for page load metrics
    const transaction = apm.startTransaction('Estimates Page', 'Component Page Load');

    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [viewBy, setViewBy] = useState('list');
    const [sortType, setSortType] = useState('cost');
    const [jsonEstimates, setJsonEstimates] = useState([]);
    const [intraPageLoad, setIntraPageLoad] = useState(false);
    const [sendEstimatesLoading, setSendEstimatesLoading] = useState(false);
    const [estimateSent, setEstimateSent] = useState(false);
    const [hideSendEstimateButton, setHideSendEstimateButton] = useState(false);
    const [showPerUnitDisclaimer, setShowPerUnitDisclaimer] = useState(false);
    const allLocations = allLocationsConst.slice();
    const styleMapListToggle = {
        borderColor: '#E9E9E9',
        paddingX: '16px',
        paddingY: '6px',
        backgroundColor: '#E9E9E9',
        '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: '#002677',
            borderColor: '#8e8585',
            color: '#ffffff',
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25)',
        },
        '&.MuiToggleButtonGroup-grouped': {
            borderRadius: '8px !important',
        },
        mx: '1px !important',
        '&:focus': {
            outline: '#196ecf auto 1px',
        },
    };

    const isJsonObject = (strData) => {
        try {
            JSON.parse(strData);
        } catch (e) {
            return false;
        }
        return true;
    };

    useEffect(() => {
        if (isJsonObject(estimateResponse)) {
            setJsonEstimates(JSON.parse(estimateResponse));
        } else {
            setJsonEstimates(estimateResponse);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [estimateResponse]);

    useEffect(() => {
        const hidingSendEstimateButton = checkForHidingSendEstimateButton(
            memberDetails,
            currentSelectedServiceForDisplay
        );
        if (hidingSendEstimateButton) {
            setHideSendEstimateButton(true);
        } else {
            setHideSendEstimateButton(false);
        }
    });

    useState(() => {
        if (
            selectedService?.value?.fullCarePath?.serviceTypes?.includes(PER_UNIT_MEASURES.perUnit)
        ) {
            setShowPerUnitDisclaimer(true);
        } else {
            setShowPerUnitDisclaimer(false);
        }
    });

    const handleClosePerUnitDisclaimer = () => {
        setShowPerUnitDisclaimer(false);
    };

    //advocate view copy success popup, fulfillment popup
    const [fulfillmentSuccessSnackbarOpen, setFulfillmentSuccessSnackbarOpen] = useState(false);
    const [fulfillmentFailureSnackbarOpen, setFulfillmentFailureSnackbarOpen] = useState(false);
    const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
    const [estimate, setEstimate] = useState();

    useEffect(() => {}, [sortType]);

    useEffect(() => {
        setOtherLocationsOpen(false);
        transaction.end();
    }, []);

    const handleOtherLocationsClose = () => {
        setOtherLocationsOpen(false);
    };

    const handleViewSingleProvider = () => {
        setSingleEstimateResponse(estimate);
        setProvider(provider);
        history.push(SINGLE_ESTIMATE_ROUTE);
    };

    const setCopySuccess = () => {
        setCopySnackbarOpen(true);
    };

    const handleCopySnackbarClose = () => {
        setCopySnackbarOpen(false);
    };

    const handleFulfillmentSuccessSnackbarClose = () => {
        setFulfillmentSuccessSnackbarOpen(false);
    };

    const handleFulfillmentFailureSnackbarClose = () => {
        setFulfillmentFailureSnackbarOpen(false);
    };

    const TablePaginationActions = (props) => {
        const theme = useTheme();
        const { count, page, rowsPerPage, onPageChange } = props;

        const handleBackButtonClick = (event) => {
            //TODO filter re-implementation will need to use 'setIntraPageLoad(false)' to reenable
            setIntraPageLoad(true);
            onPageChange(event, page - 1);
        };

        const handleNextButtonClick = (event) => {
            setIntraPageLoad(true);
            onPageChange(event, page + 1);
        };

        const handlePageDropDown = (event) => {
            setIntraPageLoad(true);
            // we're grabbing from human readable so need to offset by 1
            setEstimateResultsPage(event.target.value - 1);
        };

        return (
            <Grid container direction="row" wrap="nowrap">
                <Grid item style={{ paddingTop: '16px' }}>
                    <IconButton
                        onClick={(e) => {
                            e.preventDefault();
                            handleBackButtonClick();
                        }}
                        disabled={page <= 0}
                        aria-label="previous page"
                        style={{ fill: '#333333', paddingLeft: '0px' }}
                        sx={{
                            '&:focus': {
                                outline: '#196ecf auto 4px',
                                borderRadius: '0px',
                            },
                        }}
                    >
                        {theme.direction === 'rtl' ? <ArrowForward /> : <ArrowBack />}
                        <Typography.Footnote
                            style={{
                                marginBottom: '0px',
                                fontWeight: 700,
                                fontSize: '14.22px',
                                color: page <= 0 ? '#0000008a' : '#000000de',
                                paddingLeft: '8px',
                            }}
                        >
                            Previous
                        </Typography.Footnote>
                    </IconButton>
                </Grid>
                <Grid
                    item
                    style={{
                        paddingTop: '13px',
                    }}
                >
                    <Select
                        aria-busy={otherLocationsOpen} //suppress the announcement for dialog opening
                        onChange={handlePageDropDown}
                        value={jsonEstimates && jsonEstimates.scenarios ? page + 1 : ''}
                        style={{
                            fontWeight: 700,
                            fontSize: '14.22px',
                            paddingLeft: '24px',
                            paddingRight: '16px',
                            paddingTop: '8px',
                            paddingBottom: '8px',
                        }}
                        sx={{
                            '&:focus': {
                                outline: '#196ecf auto 1px !important',
                            },
                            '&&&:before': {
                                borderBottom: 'none',
                            },
                            '&&:after': {
                                borderBottom: 'none',
                            },
                        }}
                        variant="standard"
                    >
                        {pageSelectionOptions.map((pageOption) => (
                            <MenuItem
                                id="intraPageMenuItem"
                                value={jsonEstimates && jsonEstimates.scenarios && pageOption}
                                key={pageOption}
                                sx={{
                                    '&:focus': {
                                        outline: '#196ecf auto 1px !important',
                                    },
                                }}
                                aria-current={page + 1 == pageOption ? 'true' : 'false'}
                            >
                                Page {pageOption} of {pageSelectionOptions.length}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>
                <Grid item style={{ paddingTop: '16px' }}>
                    <IconButton
                        onClick={(e) => {
                            e.preventDefault();
                            handleNextButtonClick();
                        }}
                        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                        aria-label="next page"
                        style={{ fill: '#333333' }}
                        sx={{
                            '&:focus': {
                                outline: '#196ecf auto 1px',
                                borderRadius: '0px',
                            },
                        }}
                    >
                        <Typography.Footnote
                            style={{
                                marginBottom: '0px',
                                fontWeight: 700,
                                fontSize: '14.22px',
                                color:
                                    page >= Math.ceil(count / rowsPerPage) - 1
                                        ? '#0000008a'
                                        : '#000000de',
                                paddingRight: '8px',
                            }}
                        >
                            Next
                        </Typography.Footnote>
                        {theme.direction === 'rtl' ? <ArrowBack /> : <ArrowForward />}
                    </IconButton>
                </Grid>
            </Grid>
        );
    };

    TablePaginationActions.propTypes = {
        count: PropTypes.number.isRequired,
        onPageChange: PropTypes.func.isRequired,
        page: PropTypes.number.isRequired,
        rowsPerPage: PropTypes.number.isRequired,
    };

    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    /**
     * Checks results back from Pricing Service is valid
     */
    const checkPriceEstimateResults = () => {
        return jsonEstimates && jsonEstimates.scenarios && jsonEstimates.scenarios.length > 0;
    };

    const displayNoResults = () => {
        return (
            <Grid item xs={12} sx={classes.marginTop20}>
                <Typography.H1 align="left" className="tds-text__paragraph--bold">
                    No Results Found
                </Typography.H1>
            </Grid>
        );
    };

    /**
     * Renders the listView or MapView of multiple estimates base on the viewBy state variable
     */
    const renderListOrMapView = () => {
        //get the sorted costs and providers before rendering.
        const { sortedPageDisplayRange, sortedProviders } = sortEstimateWithProviders();

        return viewBy === 'list' || !isDesktopView ? (
            displayEstimates(sortedProviders)
        ) : (
            <MapView
                data-testid="mapContainer"
                pageDisplayRange={sortedPageDisplayRange}
                setProvider={setProvider}
                setEstimate={setEstimate}
                currentSelectedServiceForDisplay={currentSelectedServiceForDisplay}
                otherLocationsOpen={otherLocationsOpen}
                setOtherLocationsOpen={setOtherLocationsOpen}
                closestLocationIndex={closestLocationIndex}
                setClosestLocationIndex={setClosestLocationIndex}
                topLocation={topLocation}
                setTopLocation={setTopLocation}
                page={estimateResultsPage}
                rowsPerPage={rowsPerPage}
                sortType={sortType}
                sortEstimateWithProviders={sortEstimateWithProviders}
                selectedProvider={selectedProvider}
            />
        );
    };

    // creates an array containing 1-n numbered pages for display in dropdown selection menu
    // if number of estimates is not greater than 0, returns [1]
    const pageSelectionOptions =
        jsonEstimates?.scenarios?.length > 0
            ? range(1, [Math.ceil(jsonEstimates.scenarios.length / rowsPerPage) + 1])
            : [1];

    const matchProviderWithEstimate = (estimate, provider) => {
        const providerDataIdentifier = provider?.locAndPractRole[0]?.identifiers;
        const estimateDataIdentifier = estimate?.claims[0]?.procedures[0]?.provider?.identifiers;

        return (
            providerDataIdentifier?.nationalProviderId == estimateDataIdentifier?.npi &&
            providerDataIdentifier?.providerDigestId == estimateDataIdentifier?.pidd &&
            providerDataIdentifier?.mpin == estimateDataIdentifier?.mpin
        );
    };

    const handlePageChange = (event, newPage) => {
        setEstimateResultsPage(newPage);
    };

    const displayEstimate = (estimate, provider) => {
        return (
            <Estimate
                estimate={estimate}
                provider={provider}
                setEstimate={setEstimate}
                setProvider={setProvider}
                key={`estimate-${estimate.id}`}
                setOtherLocationsOpen={setOtherLocationsOpen}
                setClosestLocationIndex={setClosestLocationIndex}
                setTopLocation={setTopLocation}
                allLocations={allLocations}
            ></Estimate>
        );
    };

    const componentRef = useRef();
    const reactToPrintContent = useCallback(() => {
        return componentRef.current;
    }, []);

    const resultsGridRef = useRef();
    const reactToResultContent = useCallback(() => {
        return resultsGridRef.current;
    }, []);

    const pageStyle = `
      @page {
        size: Letter;
      }
    `;

    const onBeforeGetContentResolve = useRef();

    const handleOnBeforeGetContent = useCallback(() => {
        return new Promise((resolve) => {
            onBeforeGetContentResolve.current = resolve;

            setTimeout(() => {
                resolve();
            }, 200);
        });
    }, []);

    const onBeforeGetPrintContentResolve = useRef();

    const handleOnBeforeGetPrintContent = useCallback(() => {
        return new Promise((resolve) => {
            onBeforeGetPrintContentResolve.current = resolve;

            setTimeout(() => {
                resolve();
            }, 200);
        });
    }, []);

    const handleResultPrint = useReactToPrint({
        pageStyle: pageStyle,
        content: reactToResultContent,
        documentTitle: 'Estimate',
        onBeforeGetContent: handleOnBeforeGetPrintContent,
        removeAfterPrint: true,
    });

    const handlePrint = useReactToPrint({
        pageStyle: pageStyle,
        content: reactToPrintContent,
        documentTitle: 'Estimate',
        onBeforeGetContent: handleOnBeforeGetContent,
        removeAfterPrint: true,
    });

    const handleAlignment = (event, newAlignment) => {
        if (newAlignment !== null) {
            setViewBy(newAlignment);
        }
    };

    // // function to handle sorting based on the toggle button state.
    const handleSortType = (event, newSort) => {
        if (newSort !== null) {
            setSortType(newSort);
        }
    };

    const handleDefaultMapView = (e, viewBy) => {
        if (viewBy == 'map') {
            setSortType('distance');
        }
    };

    const handleSendEstimate = () => {
        try {
            const estimateCopy = isJsonObject(estimateResponse)
                ? JSON.parse(estimateResponse)
                : JSON.parse(JSON.stringify(estimateResponse));

            const scenarios = [];
            if (jsonEstimates && jsonEstimates.scenarios) {
                jsonEstimates.scenarios.map((scenario) => {
                    scenarios.push(JSON.parse(JSON.stringify(scenario)));
                });
            }

            const estimateInfo = formatFulfillmentRequest(
                estimateCopy,
                scenarios,
                currentSelectedServiceForDisplay,
                allProvidersPool,
                memberDetails,
                formValues
            );

            FulfillmentService.postFulfillment(estimateInfo, getIncrementedCorId())
                .then((resp) => {
                    setSendEstimatesLoading(false);
                    if (resp instanceof Error) {
                        setFulfillmentFailureSnackbarOpen(true);
                        return;
                    }
                    setFulfillmentSuccessSnackbarOpen(true);
                    setEstimateSent(true);
                })
                .catch((err) => {
                    setSendEstimatesLoading(false);
                    console.error(err);
                    setFulfillmentFailureSnackbarOpen(true);
                });
        } catch (error) {
            console.error(error);
            setFulfillmentFailureSnackbarOpen(true);
            setSendEstimatesLoading(false);
        }
    };

    const renderFulfillmentSnackbars = () => {
        if (fulfillmentSuccessSnackbarOpen) {
            return (
                <Snackbar
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    open={fulfillmentSuccessSnackbarOpen}
                    autoHideDuration={6000}
                    onClose={handleFulfillmentSuccessSnackbarClose}
                >
                    <Alert
                        onClose={handleFulfillmentSuccessSnackbarClose}
                        severity="success"
                        sx={{ width: '100%' }}
                    >
                        Successfully sent estimate for fulfillment!
                    </Alert>
                </Snackbar>
            );
        } else if (fulfillmentFailureSnackbarOpen) {
            return (
                <Snackbar
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    open={fulfillmentFailureSnackbarOpen}
                    autoHideDuration={6000}
                    onClose={handleFulfillmentFailureSnackbarClose}
                >
                    <Alert
                        onClose={handleFulfillmentFailureSnackbarClose}
                        severity="error"
                        sx={{ width: '100%' }}
                    >
                        Failed to Send Estimate
                    </Alert>
                </Snackbar>
            );
        }
    };

    /**
     * Display all the estimates with appropriate cost and providers data.
     * @param {Array} allProvidersPool - an array of providers
     */
    const displayEstimates = (allProvidersPool) => {
        const { sortedPageDisplayRange, sortedProviders } = sortEstimateWithProviders();

        let estimateCardList = sortedPageDisplayRange.map((estimate, index) => {
            if (selectedProvider.value) {
                const filteredProviderIndex = allProvidersPool.findIndex(
                    (prov) => prov?.unTranslatedText == selectedProvider?.value?.unTranslatedText
                );
                return displayEstimate(estimate, allProvidersPool[filteredProviderIndex]);
            } else {
                return displayEstimate(
                    estimate,
                    sortedProviders[index + estimateResultsPage * rowsPerPage]
                );
            }
        });

        return estimateCardList;
    };

    /**
     * Sort estimates based on costs, sort Providers based on them when Sort Type is 'Cost'.
     * Sort providers based on distance, sort estimates based on them when Sort Type is 'Distance'.
     */
    const sortEstimateWithProviders = () => {
        if (sortType === 'cost') {
            let estimateData = jsonEstimates;

            estimateData.scenarios.sort((a, b) => {
                const costShareA = a?.costShare;
                const costShareB = b?.costShare;
                // place all the estimates with no CostShare or a zero dollar rate at the very end.
                if (!costShareA || costShareA?.rate == 0) {
                    return 1;
                } else if (!costShareB || costShareB?.rate == 0) {
                    return -1;
                }
                return costShareA?.outOfPocket - costShareB?.outOfPocket;
            });

            const providersSortedByEstimate = [];
            estimateData.scenarios.map((estimate) => {
                const matchingProvider = allProvidersPool.filter((provider) => {
                    return matchProviderWithEstimate(estimate, provider);
                });
                // filter returns a list, but we need only the object inside it.
                if (matchingProvider && matchingProvider.length) {
                    providersSortedByEstimate.push(matchingProvider[0]);
                }
            });

            // calculates range of estimates to be displayed per page
            const sortedPageDisplayRange =
                estimateData && estimateData.scenarios
                    ? estimateData.scenarios.slice(
                          estimateResultsPage * rowsPerPage,
                          (estimateResultsPage + 1) * rowsPerPage
                      )
                    : [];

            return { sortedPageDisplayRange, sortedProviders: providersSortedByEstimate };
        } else if (sortType === 'distance') {
            let estimateData = jsonEstimates;

            const sortedProviders = allProvidersPool.sort((a, b) => {
                return a.locAndPractRole?.[0]?.distance - b.locAndPractRole?.[0]?.distance;
            });

            // when viewing estimate for a single provider.
            if (selectedProvider?.value) {
                const matchingSingleEstimate = estimateData.scenarios.filter((estimate) => {
                    const matchResult = matchProviderWithEstimate(estimate, provider);
                    return matchResult;
                });
                return {
                    sortedPageDisplayRange: matchingSingleEstimate,
                    sortedProviders: allProvidersPool.filter(
                        (prov) =>
                            prov?.unTranslatedText == selectedProvider?.value?.unTranslatedText
                    ),
                };
            }

            const estimatesSortedByProvider = [];
            sortedProviders.map((provider) => {
                const matchingEstimate = estimateData.scenarios.filter((estimate) => {
                    const matchResult = matchProviderWithEstimate(estimate, provider);
                    return matchResult;
                });
                // filter returns a list, but we need only the object inside it.
                if (matchingEstimate && matchingEstimate.length) {
                    estimatesSortedByProvider.push(matchingEstimate[0]);
                }
            });

            // calculates range of estimates to be displayed per page
            const sortedPageDisplayRange = estimatesSortedByProvider.slice(
                estimateResultsPage * rowsPerPage,
                (estimateResultsPage + 1) * rowsPerPage
            );

            return { sortedPageDisplayRange, sortedProviders };
        }
    };

    function SortButton() {
        return (
            <Grid item>
                <Card
                    variant="outlined"
                    style={{
                        display: 'inline-block',
                        borderRadius: '8px',
                        backgroundColor: '#E9E9E9',
                        borderColor: '#E9E9E9',
                    }}
                >
                    <ToggleButtonGroup
                        value={sortType}
                        exclusive
                        onChange={handleSortType}
                        aria-labelledby="sortBy"
                        data-testid="textAlignmentSortType"
                        sx={{
                            padding: '4px',
                            borderColor: '#E9E9E9',
                            backgroundColor: '#E9E9E9',
                        }}
                    >
                        <ToggleButton
                            value="cost"
                            data-testid="toggleCostLeftAligned"
                            sx={{
                                borderColor: '#E9E9E9',
                                paddingX: '16px',
                                paddingY: '6px',
                                backgroundColor: '#E9E9E9',
                                '&.Mui-selected, &.Mui-selected:hover': {
                                    backgroundColor: '#002677',
                                    borderColor: '#8e8585',
                                    color: '#ffffff',
                                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25)',
                                },
                                '&.MuiToggleButtonGroup-grouped': {
                                    borderRadius: '8px !important',
                                },
                                mx: '1px !important',
                                '&:focus': {
                                    outline: '#196ecf auto 1px',
                                },
                            }}
                        >
                            <Typography.Paragraph
                                style={{
                                    marginBottom: '0px',
                                    ...(sortType === 'cost' && { color: '#ffffff' }),
                                }}
                            >
                                Cost
                            </Typography.Paragraph>
                        </ToggleButton>
                        <ToggleButton
                            value="distance"
                            data-testid="toggleDistanceCentered"
                            sx={{
                                borderColor: '#E9E9E9',
                                paddingX: '16px',
                                paddingY: '6px',
                                backgroundColor: '#E9E9E9',
                                '&.Mui-selected, &.Mui-selected:hover': {
                                    backgroundColor: '#002677',
                                    borderColor: '#8e8585',
                                    color: '#ffffff',
                                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25)',
                                },
                                '&.MuiToggleButtonGroup-grouped': {
                                    borderRadius: '8px !important',
                                },
                                mx: '1px !important',
                                '&:focus': {
                                    outline: '#196ecf auto 1px',
                                },
                            }}
                        >
                            <Typography.Paragraph
                                style={{
                                    marginBottom: '0px',
                                    ...(sortType === 'distance' && { color: '#ffffff' }),
                                }}
                            >
                                Distance
                            </Typography.Paragraph>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Card>
            </Grid>
        );
    }

    const renderSendEstimatesButton = () => {
        return (
            <Button
                onClick={() => {
                    handleSendEstimate();
                    setSendEstimatesLoading(true);
                }}
                disabled={estimateSent}
                buttonType={estimateSent ? 'secondary-two' : 'secondary-one'}
                style={{
                    paddingLeft: '20px',
                    paddingRight: '20px',
                    backgroundColor: estimateSent ? '#007000' : '#FFFFFF',
                    cursor: estimateSent ? 'default' : 'pointer',
                    width: '100%',
                    color: estimateSent ? '#FFFFFF' : '#002677',
                    borderColor: estimateSent ? '#FFFFFF' : '#002677',
                }}
                loading={sendEstimatesLoading}
            >
                {sendEstimateButtonLabel(estimateSent, sendEstimatesLoading)}
            </Button>
        );
    };

    const renderViewByToggleButtons = () => {
        return (
            <>
                <Grid item>
                    <Typography.Paragraph id="viewBy" style={{ marginBottom: '0px' }}>
                        View by
                    </Typography.Paragraph>
                </Grid>
                <Grid item>
                    <Card
                        variant="outlined"
                        style={{
                            display: 'inline-block',
                            borderRadius: '8px',
                            backgroundColor: '#E9E9E9',
                            borderColor: '#E9E9E9',
                        }}
                    >
                        <ToggleButtonGroup
                            value={viewBy}
                            aria-labelledby="viewBy"
                            exclusive
                            onChange={handleAlignment}
                            data-testid="textAlignmentViewBy"
                            sx={{
                                padding: '4px',
                                borderColor: '#E9E9E9',
                                backgroundColor: '#E9E9E9',
                            }}
                        >
                            <ToggleButton
                                value="list"
                                data-testid="left aligned"
                                sx={{
                                    ...styleMapListToggle,
                                }}
                            >
                                <FormatListBulleted
                                    style={{
                                        fontSize: '16px',
                                        marginRight: '8px',
                                    }}
                                />
                                <Typography.Paragraph
                                    style={{
                                        marginBottom: '0px',
                                        ...(viewBy === 'list' && {
                                            color: '#ffffff',
                                        }),
                                    }}
                                >
                                    List
                                </Typography.Paragraph>
                            </ToggleButton>
                            <ToggleButton
                                data-testid="mapButtonCentered"
                                value="map"
                                onClick={handleDefaultMapView}
                                sx={{
                                    ...styleMapListToggle,
                                }}
                            >
                                <LocationOn
                                    style={{
                                        fontSize: '16px',
                                        marginRight: '8px',
                                    }}
                                />
                                <Typography.Paragraph
                                    style={{
                                        marginBottom: '0px',
                                        ...(viewBy === 'map' && {
                                            color: '#ffffff',
                                        }),
                                    }}
                                >
                                    Map
                                </Typography.Paragraph>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Card>
                </Grid>
            </>
        );
    };

    const serviceNameToDisplay = getServiceNameToDisplay(currentSelectedServiceForDisplay);

    const renderServiceInformation = () => {
        const styleTypography = {
            marginBottom: 0,
            paddingRight: '4px',
            fontWeight: 700,
            color: '#333333',
        };
        if (isDesktopView) {
            return (
                <Typography.H4
                    style={{
                        ...styleTypography,
                    }}
                >
                    {serviceNameToDisplay}
                </Typography.H4>
            );
        } else {
            return (
                <Typography.H3
                    style={{
                        ...styleTypography,
                    }}
                >
                    {serviceNameToDisplay}
                </Typography.H3>
            );
        }
    };

    const renderSuccessCopySnackbar = () => {
        return (
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={copySnackbarOpen}
                autoHideDuration={2000}
                onClose={handleCopySnackbarClose}
            >
                <Alert onClose={handleCopySnackbarClose} severity="success" sx={{ width: '100%' }}>
                    Copied to Clipboard&#33;
                </Alert>
            </Snackbar>
        );
    };

    const renderStartNewSearchLink = () => {
        return (
            <Typography.Link
                style={{ cursor: 'pointer' }}
                onClick={() => {
                    history.push(SEARCH_ROUTE);
                    setLoading(false);
                    handleChangeCustom(null, 'selectedService');
                    handleChangeCustom(null, 'selectedProvider');
                    clearPreventiveFlags();
                }}
                onKeyDown={(e) => {
                    if (e.key == 'Enter') {
                        history.push(SEARCH_ROUTE);
                        setLoading(false);
                        handleChangeCustom(null, 'selectedService');
                        handleChangeCustom(null, 'selectedProvider');
                        clearPreventiveFlags();
                    }
                }}
                tabIndex="0"
            >
                Start a new search
            </Typography.Link>
        );
    };

    const hasPreventive = selectedService?.value?.fullCarePath?.preventive;

    return (
        <Box sx={{ width: '100%' }} data-testid="estimatesPage">
            {renderFulfillmentSnackbars()}
            {renderSuccessCopySnackbar()}
            <Grid item xs={12} ref={ref}>
                <Grid container>
                    <Grid item>
                        <UtilityIcon size="medium" color="primary" icon={ArrowBack} />
                    </Grid>
                    <Grid item>{renderStartNewSearchLink()}</Grid>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <Typography.H1 align="left" data-testid="searchResultsHeader">
                    Your search results
                </Typography.H1>
            </Grid>
            <Grid item xs={12}>
                <Divider sx={classes.divider} data-testid="headerDivider"></Divider>
            </Grid>
            <Grid container direction="column">
                <Grid item xs={12}>
                    <Grid
                        container
                        direction="row"
                        style={{ paddingBottom: '24px', paddingTop: '32px' }}
                    >
                        <Grid item xs={12}>
                            <Grid container justifyContent="space-between">
                                <div
                                    aria-busy={intraPageLoad || otherLocationsOpen} //disable for intra-page loading or dialog opening
                                    aria-live="polite" //announce filter / initial search results
                                    aria-atomic="true" //assert to capture child element
                                    aria-owns="cptCode" //set accessibility tree ownership
                                    aria-labelledby="resultsFor cptCode" //concatenate element contents for alert
                                >
                                    <Typography.H6
                                        id="resultsFor"
                                        data-testid="resultsFor"
                                        style={{
                                            marginBottom: 0,
                                            paddingBottom: '4px',
                                            fontWeight: 700,
                                            color: '#333333',
                                        }}
                                    >
                                        {jsonEstimates && jsonEstimates.scenarios
                                            ? jsonEstimates.scenarios.length
                                            : 0}{' '}
                                        results for
                                    </Typography.H6>
                                </div>

                                <Grid item xs={4} hidden={!isDesktopView}>
                                    {integrationType === 'member' && (
                                        <Grid
                                            container
                                            justifyContent="flex-end"
                                            sx={{
                                                [`@media print`]: {
                                                    display: 'none',
                                                },
                                            }}
                                        >
                                            <PrintEstimateOrResultsButton
                                                style={{}}
                                                text="Print Results"
                                                handlePrint={handleResultPrint}
                                            />
                                        </Grid>
                                    )}
                                    {integrationType === 'advocate' && (
                                        <Grid item>
                                            <Grid
                                                container
                                                direction="row"
                                                spacing={1}
                                                justifyContent="flex-end"
                                                sx={{
                                                    paddingTop: '6px',
                                                    paddingBottom: '2px',
                                                }}
                                            >
                                                {!hideSendEstimateButton && (
                                                    <Grid item xs="auto">
                                                        {renderSendEstimatesButton()}
                                                    </Grid>
                                                )}
                                                <Grid item>
                                                    <CopyEstimate
                                                        estimate={
                                                            jsonEstimates
                                                                ? jsonEstimates.scenarios
                                                                : []
                                                        } //Additional logic for INN results not implemented
                                                        provider={allProvidersPool} // TODO feels wrong
                                                        estimateType={'multiple'}
                                                        setCopySuccess={setCopySuccess}
                                                    ></CopyEstimate>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid id="cptCode" item xs={10}>
                            <Grid container direction="row">
                                <Grid item xs={12}>
                                    {renderServiceInformation()}
                                </Grid>
                            </Grid>
                        </Grid>
                        {hasPreventive && (
                            <EstimateToggleComponent
                                setLoading={setLoading}
                                loading={loading}
                                setProvider={setProvider}
                                setCurrentSelectedServiceForDisplay={
                                    setCurrentSelectedServiceForDisplay
                                }
                                toggleType={'PreventiveAndDiagnostic'}
                            />
                        )}
                    </Grid>
                </Grid>
                {showPerUnitDisclaimer && (
                    <PerUnitDisclaimer
                        handleClosePerUnitDisclaimer={handleClosePerUnitDisclaimer}
                    />
                )}
                <AgeRestrictionDisclaimer />
                <Grid item>
                    <Grid container>
                        {jsonEstimates?.scenarios?.length > 0 &&
                            !isIndemnity[
                                selectedService?.value?.fullCarePath?.coverageTypeCode
                            ] && (
                                <GeoAverageCost
                                    geoAverageCost={jsonEstimates.scenarios?.[0]?.geoAverageCost}
                                    carePathServiceTypes={
                                        selectedService?.value?.fullCarePath?.serviceTypes
                                    }
                                />
                            )}
                    </Grid>
                </Grid>
                <Grid item>
                    <Grid container justifyContent="flex-end">
                        <Grid item>
                            <Grid
                                container
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{ marginRight: '32px' }}
                            >
                                <Grid item>
                                    <Typography.Paragraph
                                        id="sortBy"
                                        style={{ marginBottom: '0px' }}
                                    >
                                        Sort by
                                    </Typography.Paragraph>
                                </Grid>
                                <SortButton />
                            </Grid>
                        </Grid>
                        <Grid
                            item
                            data-testid="viewBy"
                            style={{ display: isDesktopView ? 'block' : 'none' }}
                        >
                            <Grid container direction="row" alignItems="center" spacing={1}>
                                {renderViewByToggleButtons()}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
            >
                <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
                    Fulfillment message successfully sent to Communications Hub!
                </Alert>
            </Snackbar>
            <Grid container style={{ borderBottom: '1px solid #E0E0E0' }}></Grid>
            {jsonEstimates &&
                jsonEstimates.scenarios &&
                allProvidersPool &&
                allProvidersPool.length == 1 &&
                allProvidersPool[0].providerNetworkRestriction == 'outOfNetwork' && (
                    <Alert severity="error" sx={classes.marginTop10}>
                        <AlertTitle>{allProvidersPool[0].unTranslatedText}</AlertTitle>
                        This doctor may be <strong>out-of-network</strong> so your costs may be
                        higher than an in-network provider.
                    </Alert>
                )}
            <Box ref={resultsGridRef}>
                <Grid container>
                    <Grid
                        item
                        xs={12}
                        sx={{
                            overflowY: 'scroll',
                            maxHeight: '880px',
                            [`@media print`]: {
                                width: '100%',
                                height: '100% !important',
                                overflow: 'visible',
                            },
                        }}
                    >
                        {checkPriceEstimateResults() ? renderListOrMapView() : displayNoResults()}
                    </Grid>
                </Grid>
            </Box>
            <TablePagination
                component="div"
                count={
                    jsonEstimates && jsonEstimates.scenarios
                        ? jsonEstimates.scenarios.length == 1
                            ? 1
                            : allProvidersPool.length
                        : 0
                }
                page={estimateResultsPage}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[]}
                labelRowsPerPage=""
                onPageChange={handlePageChange}
                ActionsComponent={TablePaginationActions}
                labelDisplayedRows={() => {
                    '';
                }}
                role="navigation"
            />
            <Divider data-testid="headerDivider" hidden={isDesktopView} aria-hidden="true" />
            <Grid
                container
                direction="column"
                sx={{
                    paddingTop: '16px',
                }}
            >
                {!isDesktopView && integrationType === 'member' && (
                    <Grid item>
                        <Box
                            sx={{
                                [`@media print`]: {
                                    display: 'none',
                                },
                                whiteSpace: 'nowrap',
                                marginBottom: '32px',
                            }}
                        >
                            <PrintEstimateOrResultsButton
                                style={{
                                    height: 'auto',
                                    paddingTop: '6px',
                                    paddingBottom: '2px',
                                    minWidth: 'auto',
                                    width: '100%',
                                }}
                                text="Print Estimate"
                                handlePrint={handlePrint}
                            />
                        </Box>
                    </Grid>
                )}
                {!isDesktopView && integrationType === 'advocate' && (
                    <Grid item>
                        <Box
                            sx={{
                                whiteSpace: 'nowrap',
                                marginBottom: '8px',
                                [`@media print`]: {
                                    display: 'none',
                                },
                                height: '40px',
                            }}
                        >
                            {!hideSendEstimateButton && renderSendEstimatesButton()}
                        </Box>
                        <Box
                            sx={{
                                whiteSpace: 'nowrap',
                                marginBottom: '32px',
                                [`@media print`]: {
                                    display: 'none',
                                },
                                height: '40px',
                            }}
                        >
                            <CopyEstimate
                                estimate={jsonEstimates ? jsonEstimates.scenarios : []} //Additional logic for INN results not implemented
                                provider={allProvidersPool} // TODO feels wrong
                                estimateType={'multiple'}
                                setCopySuccess={setCopySuccess}
                            />
                        </Box>
                    </Grid>
                )}
            </Grid>
            {provider ? (
                <OtherLocationsModal
                    estimate={estimate}
                    setEstimate={setEstimate}
                    provider={provider}
                    handleViewSingleProvider={handleViewSingleProvider}
                    topLocation={topLocation}
                    setTopLocation={setTopLocation}
                    otherLocationsOpen={otherLocationsOpen}
                    handleOtherLocationsClose={handleOtherLocationsClose}
                    key={`estimate-${estimate ? estimate.id : 0}`}
                />
            ) : null}
        </Box>
    );
}
